// src/components/ScriptTranslator.js 수정

import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../context/AuthContext';
import '../../ScriptTranslator.css';

const ScriptTranslator = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [script, setScript] = useState('');
  const [translationLanguage, setTranslationLanguage] = useState('Korean');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // 게스트(비로그인) 사용자는 번역 결과가 서버에 저장되지 않으므로, 이 화면에서 바로 보여주기 위한 상태다.
  const [guestResult, setGuestResult] = useState(null);
  
  // 최대 글자수 제한
  const MAX_CHARS = 2000;
  
  // 지원되는 번역 언어 목록
  const supportedLanguages = [
    { value: 'Korean', label: t('languages.korean') }, 
    { value: 'English', label: t('languages.english') },
    { value: 'Japanese', label: t('languages.japanese') },
    { value: 'Chinese', label: t('languages.chinese') },
    { value: 'Spanish', label: t('languages.spanish') },
    { value: 'French', label: t('languages.french') },
    { value: 'German', label: t('languages.german') }
  ];
  
  // 스크립트 내용 변경 핸들러
  const handleScriptChange = (e) => {
    const newValue = e.target.value;
    // 최대 글자수 제한 적용
    if (newValue.length <= MAX_CHARS) {
      setScript(newValue);
    }
  };
  
  // 남은 글자수 계산
  const remainingChars = MAX_CHARS - script.length;
  
  // 스크립트 제출 처리
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!script.trim()) {
      setError(t('script.emptyScriptError'));
      return;
    }
    
    setLoading(true);
    setError(null);
    setGuestResult(null);
    
    try {
      const response = await api.post('/api/script/translate', {
        script,
        translationLanguage
      });
      
      if (user) {
        // 로그인 사용자는 서버에 결과가 저장되므로 결과 페이지로 이동한다.
        navigate(`/script/result/${response.data.id}`);
      } else {
        // 게스트는 결과가 저장되지 않으므로, 이 화면에서 바로 번역 결과를 보여준다.
        setGuestResult(response.data);
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        // 비로그인 체험을 다 써버렸을 때 백엔드가 401과 함께 이 메시지를 보낸다.
        setError(err.response.data?.message || '무료 체험을 모두 사용했습니다. 로그인 후 이용해주세요.');
      } else {
        setError(err.response?.data?.message || t('script.translationError'));
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="script-translator-container">
      <h1 className="script-translator-title">{t('script.title')}</h1>
      
      {!user && (
        <p className="guest-notice">
          로그인하지 않아도 일주일에 한 번은 무료로 번역해볼 수 있습니다. 결과를 저장하거나 오디오를 받으려면 로그인이 필요합니다.
        </p>
      )}
      
      {error && <div className="error-message">{error}</div>}
      
      <form className="script-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="script">
            {t('script.enterScript')}  
            <span className={`char-counter ${remainingChars < 100 ? 'warning' : ''}`}>
               {remainingChars} / {MAX_CHARS}
            </span>
          </label>
          <textarea
            id="script"
            className="script-textarea"
            value={script}
            onChange={handleScriptChange}
            placeholder={t('script.scriptPlaceholder')}
            rows={10}
            maxLength={MAX_CHARS}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="translationLanguage">{t('script.translationLanguage')}</label>
          <select
            id="translationLanguage"
            className="language-select"
            value={translationLanguage}
            onChange={(e) => setTranslationLanguage(e.target.value)}
          >
            {supportedLanguages.map(lang => (
              <option key={lang.value} value={lang.value}>
                {lang.label}
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-buttons">
          <button
            type="button"
            className="cancel-button"
            onClick={() => navigate('/')}
          >
            {t('common.cancel')}
          </button>
          
          <button
            type="submit"
            className="translate-button"
            disabled={loading || script.length === 0}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                {t('common.processing')}
              </>
            ) : (
              t('script.translate')
            )}
          </button>
        </div>
      </form>
      
      {!guestResult && (
        <div className="info-section">
          <h3>{t('script.howItWorks')}</h3>
          <ul>
            <li>{t('script.feature1')}</li>
            <li>{t('script.feature2')}</li>
            <li>{t('script.feature3')}</li>
            <li>{t('script.feature4')}</li>
            <li>{t('script.feature5')}</li>
          </ul>
        </div>
      )}

      {/* 게스트 번역 결과: 서버에 저장되지 않으므로 이 화면에서 바로 보여준다. */}
      {guestResult && (
        <div className="paragraphs-container">
          <h2>{t('script.analysisResult') || '번역 결과'}</h2>
          {guestResult.paragraphs && guestResult.paragraphs.map((paragraph, index) => (
            <div key={index} className="paragraph-card">
              <h3 className="paragraph-title">{t('script.paragraph') || '문단'} {index + 1}</h3>

              <div className="paragraph-section">
                <h4>{t('script.original') || '원문'}</h4>
                <p className="paragraph-content original">{paragraph.originalText}</p>
              </div>

              <div className="paragraph-section">
                <h4>{t('script.translation') || '번역'}</h4>
                <p className="paragraph-content translation">{paragraph.translation}</p>
              </div>

              <div className="paragraph-section">
                <h4>{t('script.summary') || '요약'}</h4>
                <p className="paragraph-content summary">{paragraph.summary}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScriptTranslator;
